import { useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid2';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import useTheme from '@mui/material/styles/useTheme';
import TextField from '@mui/material/TextField';
import useMediaQuery from '@mui/material/useMediaQuery';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { schema } from './settingsSchema';
import ActionButton from '../../elements/action-button/ActionButton';

const HealthCheckForm = ({ healthSettings, onSaveSettings }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      checkInterval: '',
      renoticeInterval: '',
      checkMethod: 'PING',
    },
    resolver: yupResolver(schema()),
  });

  useEffect(() => {
    setValue('checkInterval', healthSettings?.checkInterval || '');
    setValue('renoticeInterval', healthSettings?.renoticeInterval || '');
    setValue('checkMethod', healthSettings?.checkMethod || 'PING');
  }, [healthSettings]);

  return (
    <form>
      <Grid container spacing={2} style={{ marginTop: '1em' }}>
        <Grid
          sx={{ marginBottom: 1 }}
          size={{
            sm: 12,
            md: 4,
          }}
        >
          <Controller
            name="checkInterval"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label={t('monitor.checkInterval', { ns: 'fields' })}
                variant="outlined"
                size="small"
                error={!!errors?.checkInterval}
                helperText={errors?.checkInterval?.message}
                fullWidth
              />
            )}
          />
        </Grid>
        <Grid
          sx={{ marginBottom: 1 }}
          size={{
            sm: 12,
            md: 4,
          }}
        >
          <Controller
            name="renoticeInterval"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                label={t('monitor.renoticeInterval', { ns: 'fields' })}
                variant="outlined"
                size="small"
                error={!!errors?.renoticeInterval}
                helperText={errors?.renoticeInterval?.message}
                fullWidth
              />
            )}
          />
        </Grid>
        <Grid
          size={{
            sm: 12,
            md: 4,
          }}
        >
          <FormControl
            variant="outlined"
            size="small"
            fullWidth
            error={!!errors?.checkMethod}
          >
            <InputLabel>
              {t('monitor.checkMethod', { ns: 'fields' })}
            </InputLabel>
            <Controller
              name="checkMethod"
              control={control}
              defaultValue="PING"
              render={({ field }) => (
                <Select {...field} label="Check Method">
                  <MenuItem value="PING">PING</MenuItem>
                </Select>
              )}
            />
            {!!errors?.checkMethod && (
              <FormHelperText>{errors.checkMethod.message}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid
          style={{ paddingTop: 0 }}
          size={{
            sm: 12,
            md: 3,
          }}
        >
          <ActionButton
            type="submit"
            clickAction={handleSubmit(onSaveSettings)}
            style={{ width: isSmallScreen && '100%' }}
          >
            {t('saveSettings', { ns: 'buttons' })}
          </ActionButton>
        </Grid>
      </Grid>
    </form>
  );
};

HealthCheckForm.propTypes = {
  healthSettings: PropTypes.object,
  onSaveSettings: PropTypes.func.isRequired,
};

export default HealthCheckForm;
